import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import bgImg from "../essets/login-bgimage.png";
import logoImg from "../essets/logo.png";

import "../Styles/AdminLogin.css";
import { authAPI } from "../api/index";

// PDF documents
import privacyPolicyPdf from "../essets/App Privacy Policy.pdf";
import termsConditionsPdf from "../essets/App Privacy Policy.pdf";

function Login() {
  useEffect(() => {
    document.body.classList.add("login-page");

    return () => {
      document.body.classList.remove("login-page");
    };
  }, []);

  const navigate = useNavigate();

  // ============================================================
  // VIEW
  // ============================================================

  // view:
  // 'login'
  // 'forgotEmail'
  // 'forgotSent'
  const [view, setView] = useState("login");

  // ============================================================
  // LOGIN STATE
  // ============================================================

  const [role, setRole] = useState("super_admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // ============================================================
  // FORGOT PASSWORD STATE
  // ============================================================

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResponseMessage, setForgotResponseMessage] = useState("");

  // ============================================================
  // DELETE ACCOUNT STATE
  // ============================================================

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResponseMessage, setDeleteResponseMessage] = useState("");
  const [deleteMessageType, setDeleteMessageType] = useState("");

  // ============================================================
  // CAPTCHA
  // ============================================================

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaInput("");

    try {
      const res = await authAPI.getCaptcha();

      setCaptchaToken(res.data.captcha_token || "");
      setCaptchaQuestion(res.data.question || "");
    } catch (err) {
      toast.error("Could not load captcha. Please refresh.");
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const handleRefreshCaptcha = () => {
    fetchCaptcha();
  };

  // ============================================================
  // LOGIN SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill all fields");
      return;
    }

    if (!captchaInput.trim()) {
      toast.error("Please answer the captcha");
      return;
    }

    setLoading(true);

    try {
      const res = await authAPI.login({
        role,
        email,
        password,
        captcha_token: captchaToken,
        captcha_answer: captchaInput.trim(),
      });

      const {
        access_token,
        token,
        refresh_token,
        user,
      } = res.data;

      const finalToken = access_token || token;

      localStorage.setItem("authToken", finalToken);

      if (refresh_token) {
        localStorage.setItem("refreshToken", refresh_token);
      }

      localStorage.setItem("isAdminLoggedIn", "true");
      localStorage.setItem("adminEmail", user?.email || email);
      localStorage.setItem("adminRole", user?.role || role);

      toast.success("Login Successful");

      setTimeout(() => {
        const effectiveRole = user?.role || role;

        navigate(
          effectiveRole === "super_admin"
            ? "/dashboard"
            : "/AdminDashboard"
        );
      }, 1200);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Invalid email, password, or captcha"
      );

      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FORGOT PASSWORD
  // ============================================================

  const openForgotPassword = () => {
    setForgotEmail(email || "");
    setForgotResponseMessage("");
    setView("forgotEmail");
  };

  const backToLogin = () => {
    setView("login");
  };

  const handleSendResetRequest = async (e) => {
    e.preventDefault();

    if (!forgotEmail.trim()) {
      toast.error("Enter your registered email");
      return;
    }

    setForgotLoading(true);

    try {
      const res = await authAPI.forgotPassword(
        forgotEmail.trim()
      );

      setForgotResponseMessage(
        res.data?.message ||
          "If that email is registered, password reset instructions have been sent."
      );

      toast.success("Request submitted");

      setView("forgotSent");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Could not process request. Try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // ============================================================
  // DELETE ACCOUNT
  // ============================================================

  const openDeleteAccount = () => {
    setDeleteEmail("");
    setDeleteResponseMessage("");
    setDeleteMessageType("");
    setShowDeleteModal(true);
  };

  const closeDeleteAccount = () => {
    if (deleteLoading) return;

    setShowDeleteModal(false);
    setDeleteEmail("");
    setDeleteResponseMessage("");
    setDeleteMessageType("");
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    const trimmedEmail = deleteEmail.trim();

    if (!trimmedEmail) {
      setDeleteResponseMessage(
        "Please enter your email address."
      );
      setDeleteMessageType("error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      setDeleteResponseMessage(
        "Please enter a valid email address."
      );
      setDeleteMessageType("error");
      return;
    }

    setDeleteLoading(true);
    setDeleteResponseMessage("");
    setDeleteMessageType("");

    try {
      /*
       * IMPORTANT:
       *
       * Change this URL if your backend uses another endpoint.
       *
       * Example:
       * /api/auth/delete-account
       * /api/account/delete-request
       * /auth/delete-account-request
       */

      const response = await fetch(
        "/api/account/delete-request",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            email: trimmedEmail,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.detail ||
            "Unable to submit account deletion request."
        );
      }

      setDeleteResponseMessage(
        data?.message ||
          "Your account deletion request has been submitted successfully."
      );

      setDeleteMessageType("success");

      toast.success("Deletion request submitted");

      setDeleteEmail("");

      // Close modal after successful submission
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeleteResponseMessage("");
        setDeleteMessageType("");
      }, 2500);
    } catch (err) {
      console.error(
        "Delete account request error:",
        err
      );

      setDeleteResponseMessage(
        err.message ||
          "Something went wrong. Please try again later."
      );

      setDeleteMessageType("error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ============================================================
  // RETURN
  // ============================================================

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />

      <div className="log123-page-wrapper">

        {/* Background */}
        <img
          className="log123-bg-img"
          src={bgImg}
          alt="MLA background"
        />

        <div className="log123-bg-overlay"></div>

        <div className="log123-content">

          {/* ====================================================
              TOP BAR
          ==================================================== */}

          <div className="log123-topbar">

            <div className="log123-logo-block">

              <img
                className="log123-logo-img"
                src={logoImg}
                alt="logo"
              />

              <div className="log123-logo-text">

                <h1>INTURI</h1>

                <h2>Nageswara Rao</h2>

                <div className="log123-logo-tag">

                  <span className="log123-tag-line"></span>

                  KANDUKUR CONSTITUENCY

                  <span className="log123-tag-line"></span>

                </div>

              </div>

            </div>

            <svg
              className="log123-map-icon"
              width="90"
              height="100"
              viewBox="0 0 200 220"
              fill="none"
            ></svg>

          </div>

          {/* ====================================================
              MAIN ROW
          ==================================================== */}

          <div className="log123-main-row">

            {/* LEFT INFORMATION */}

            <div className="log123-left-info">

              <h2 className="log123-headline">

                Committed to <br />

                <span className="log123-headline-accent">
                  People.
                </span>

                <br />

                Dedicated to <br />

                <span className="log123-headline-accent">
                  Development.
                </span>

              </h2>

              <p className="log123-headline-desc">
                Building a modern, transparent and welfare-driven
                Kandukur that serves as a benchmark for regional
                development.
              </p>

              <div className="log123-badges-row">

                {/* Badge 1 */}

                <div className="log123-badge">

                  <div className="log123-badge-icon">

                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 2 4 5v6c0 5.25 3.4 9.74 8 11 4.6-1.26 8-5.75 8-11V5l-8-3z"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />

                      <rect
                        x="9.5"
                        y="10.5"
                        width="5"
                        height="4"
                        rx="1"
                        stroke="#FFD129"
                        strokeWidth="1.4"
                      />

                      <path
                        d="M10.3 10.5V9a1.7 1.7 0 013.4 0v1.5"
                        stroke="#FFD129"
                        strokeWidth="1.4"
                      />
                    </svg>

                  </div>

                  <span>
                    Secure
                    <br />
                    Access
                  </span>

                </div>

                {/* Badge 2 */}

                <div className="log123-badge">

                  <div className="log123-badge-icon">

                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="9"
                        cy="8"
                        r="3.2"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                      />

                      <path
                        d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />

                      <circle
                        cx="17"
                        cy="8"
                        r="2.6"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                      />

                      <path
                        d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>

                  </div>

                  <span>
                    Authorized
                    <br />
                    Users Only
                  </span>

                </div>

                {/* Badge 3 */}

                <div className="log123-badge">

                  <div className="log123-badge-icon">

                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="12"
                        rx="2"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                      />

                      <path
                        d="M8 20h8M12 16v4"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />

                      <path
                        d="M7 10l3 3 6-6"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                  </div>

                  <span>
                    24/7
                    <br />
                    Monitoring
                  </span>

                </div>

                {/* Badge 4 */}

                <div className="log123-badge">

                  <div className="log123-badge-icon">

                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 2 4 5v6c0 5.25 3.4 9.74 8 11 4.6-1.26 8-5.75 8-11V5l-8-3z"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />

                      <path
                        d="M9 12l2 2 4-4"
                        stroke="#FFD129"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                  </div>

                  <span>
                    Data
                    <br />
                    Protected
                  </span>

                </div>

              </div>

            </div>

            {/* ====================================================
                LOGIN CARD
            ==================================================== */}

            <div className="log123-card">

              <div className="log123-avatar-wrap">

                <div className="log123-avatar-circle">

                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="8"
                      r="4"
                      stroke="#0b3d24"
                      strokeWidth="1.8"
                    />

                    <path
                      d="M4 21v-1a8 8 0 0116 0v1"
                      stroke="#0b3d24"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>

                </div>

                <div className="log123-avatar-badge">

                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <rect
                      x="5"
                      y="11"
                      width="14"
                      height="9"
                      rx="2"
                      stroke="#0b3d24"
                      strokeWidth="1.8"
                    />

                    <path
                      d="M8 11V8a4 4 0 018 0v3"
                      stroke="#0b3d24"
                      strokeWidth="1.8"
                    />
                  </svg>

                </div>

              </div>

              <div
                key={view}
                className="log123-view-anim"
              >

                {/* ==================================================
                    LOGIN VIEW
                ================================================== */}

                {view === "login" && (
                  <>
                    <h2 className="log123-right-title">
                      Admin Login
                    </h2>

                    <p className="log123-right-subtitle">
                      Welcome back! Please login to access
                      <br />
                      <strong>MLA Dashboard</strong>
                    </p>

                    <form
                      onSubmit={handleSubmit}
                      autoComplete="off"
                    >

                      {/* Role */}

                      <div className="log123-input-group">

                        <label className="log123-label">
                          Login As{" "}
                          <span className="log123-required">
                            *
                          </span>
                        </label>

                        <div className="log123-role-group">

                          <label
                            className={`log123-role-option ${
                              role === "super_admin"
                                ? "log123-role-active"
                                : ""
                            }`}
                          >

                            <input
                              type="radio"
                              name="role"
                              value="super_admin"
                              checked={
                                role === "super_admin"
                              }
                              onChange={(e) =>
                                setRole(e.target.value)
                              }
                              className="log123-role-radio"
                            />

                            <span className="log123-role-icon">

                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12 2 4 5v6c0 5.25 3.4 9.74 8 11 4.6-1.26 8-5.75 8-11V5l-8-3z"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinejoin="round"
                                />
                              </svg>

                            </span>

                            <span className="log123-role-text">
                              Super Admin
                            </span>

                          </label>

                          <label
                            className={`log123-role-option ${
                              role === "admin"
                                ? "log123-role-active"
                                : ""
                            }`}
                          >

                            <input
                              type="radio"
                              name="role"
                              value="admin"
                              checked={
                                role === "admin"
                              }
                              onChange={(e) =>
                                setRole(e.target.value)
                              }
                              className="log123-role-radio"
                            />

                            <span className="log123-role-icon">

                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  cx="12"
                                  cy="8"
                                  r="4"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                />

                                <path
                                  d="M4 21v-1a7 7 0 0114 0v1"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                />
                              </svg>

                            </span>

                            <span className="log123-role-text">
                              Admin
                            </span>

                          </label>

                        </div>

                      </div>

                      {/* Email */}

                      <div className="log123-input-group">

                        <label className="log123-label">
                          Email Address{" "}
                          <span className="log123-required">
                            *
                          </span>
                        </label>

                        <div className="log123-input-box">

                          <span className="log123-input-icon">

                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <rect
                                x="3"
                                y="5"
                                width="18"
                                height="14"
                                rx="2"
                                stroke="#9ca3af"
                                strokeWidth="1.6"
                              />

                              <path
                                d="M3 7l9 6 9-6"
                                stroke="#9ca3af"
                                strokeWidth="1.6"
                                strokeLinejoin="round"
                              />
                            </svg>

                          </span>

                          <input
                            type="email"
                            placeholder="Enter your email address"
                            value={email}
                            onChange={(e) =>
                              setEmail(e.target.value)
                            }
                            className="log123-input"
                          />

                        </div>

                      </div>

                      {/* Password */}

                      <div className="log123-input-group">

                        <label className="log123-label">
                          Password{" "}
                          <span className="log123-required">
                            *
                          </span>
                        </label>

                        <div className="log123-input-box">

                          <span className="log123-input-icon">

                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <rect
                                x="4.5"
                                y="10.5"
                                width="15"
                                height="10"
                                rx="2"
                                stroke="#9ca3af"
                                strokeWidth="1.8"
                              />

                              <path
                                d="M8 10.5V7a4 4 0 018 0v3.5"
                                stroke="#9ca3af"
                                strokeWidth="1.8"
                              />
                            </svg>

                          </span>

                          <input
                            type={
                              showPassword
                                ? "text"
                                : "password"
                            }
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) =>
                              setPassword(e.target.value)
                            }
                            className="log123-input"
                          />

                          <button
                            type="button"
                            className="log123-eye-btn"
                            onClick={() =>
                              setShowPassword(
                                (prev) => !prev
                              )
                            }
                            tabIndex={-1}
                          >

                            {showPassword ? (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8ZM12 15a3 3 0 100-6 3 3 0 000 6Z"
                                  stroke="#9ca3af"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a20.6 20.6 0 015.06-5.94M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a20.6 20.6 0 01-2.16 3.19M14.12 14.12a3 3 0 11-4.24-4.24M1 1l22 22"
                                  stroke="#9ca3af"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}

                          </button>

                        </div>

                      </div>

                      {/* Forgot Password */}

                      <div className="log123-forgot">

                        <a
                          href="#!"
                          onClick={(e) => {
                            e.preventDefault();
                            openForgotPassword();
                          }}
                        >
                          Forgot Password?
                        </a>

                      </div>

                      {/* Captcha */}

                      <div className="log123-captcha">

                        <button
                          type="button"
                          className="log123-captcha-refresh"
                          onClick={
                            handleRefreshCaptcha
                          }
                          title="Refresh Captcha"
                          disabled={captchaLoading}
                        >

                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"
                              stroke="#0b3d24"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>

                        </button>

                        <span className="log123-captcha-text">

                          {captchaLoading
                            ? "Loading..."
                            : captchaQuestion
                            ? `${captchaQuestion} = ?`
                            : "Captcha unavailable"}

                        </span>

                        <input
                          type="text"
                          placeholder="Enter result"
                          value={captchaInput}
                          onChange={(e) =>
                            setCaptchaInput(
                              e.target.value
                            )
                          }
                          className="log123-captcha-input"
                        />

                      </div>

                      {/* Login Button */}

                      <button
                        type="submit"
                        className="log123-submit-btn log123-btn-press"
                        disabled={
                          loading ||
                          captchaLoading
                        }
                      >

                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <rect
                            x="4.5"
                            y="10.5"
                            width="15"
                            height="10"
                            rx="2"
                            stroke="#fff"
                            strokeWidth="1.8"
                          />

                          <path
                            d="M8 10.5V7a4 4 0 018 0v3.5"
                            stroke="#fff"
                            strokeWidth="1.8"
                          />
                        </svg>

                        {loading
                          ? "Signing In..."
                          : "Login Securely"}

                      </button>

                    </form>
                  </>
                )}

                {/* ==================================================
                    FORGOT PASSWORD
                ================================================== */}

                {view === "forgotEmail" && (
                  <>
                    <h2 className="log123-right-title">
                      Forgot Password
                    </h2>

                    <p className="log123-right-subtitle">
                      Enter your registered email — we'll send
                      <br />
                      password reset instructions
                    </p>

                    <form
                      onSubmit={
                        handleSendResetRequest
                      }
                      autoComplete="off"
                    >

                      <div className="log123-input-group">

                        <label className="log123-label">
                          Email Address{" "}
                          <span className="log123-required">
                            *
                          </span>
                        </label>

                        <div className="log123-input-box">

                          <span className="log123-input-icon">

                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <rect
                                x="3"
                                y="5"
                                width="18"
                                height="14"
                                rx="2"
                                stroke="#9ca3af"
                                strokeWidth="1.6"
                              />

                              <path
                                d="M3 7l9 6 9-6"
                                stroke="#9ca3af"
                                strokeWidth="1.6"
                                strokeLinejoin="round"
                              />
                            </svg>

                          </span>

                          <input
                            type="email"
                            placeholder="Enter your registered email"
                            value={forgotEmail}
                            onChange={(e) =>
                              setForgotEmail(
                                e.target.value
                              )
                            }
                            className="log123-input"
                            autoFocus
                          />

                        </div>

                      </div>

                      <button
                        type="submit"
                        className="log123-submit-btn log123-btn-press"
                        disabled={forgotLoading}
                        style={{ marginTop: 8 }}
                      >
                        {forgotLoading
                          ? "Submitting..."
                          : "Send Reset Request"}
                      </button>

                      <BackToLoginLink
                        onClick={backToLogin}
                      />

                    </form>
                  </>
                )}

                {/* ==================================================
                    FORGOT SENT
                ================================================== */}

                {view === "forgotSent" && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px 0",
                    }}
                  >

                    <div className="log123-success-circle">

                      <svg
                        width="30"
                        height="30"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13l4 4L19 7"
                          stroke="#FFD129"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                    </div>

                    <h2 className="log123-right-title">
                      Request Sent
                    </h2>

                    <p className="log123-right-subtitle">
                      {forgotResponseMessage}
                    </p>

                    <BackToLoginLink
                      onClick={backToLogin}
                    />

                  </div>
                )}

              </div>

              {/* Need Help */}

              <div className="log123-need-help">

                <p className="log123-need-help-title">
                  Need Help?
                </p>

                <div className="log123-contact-row">

                  <span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.7a16 16 0 006 6l1.2-1.2a2 2 0 012.1-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z"
                        stroke="#0b3d24"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                    </svg>

                    +91 9394141433
                  </span>

                  <span className="log123-contact-divider">
                    |
                  </span>

                  <span>

                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                        stroke="#0b3d24"
                        strokeWidth="1.6"
                      />

                      <path
                        d="M3 7l9 6 9-6"
                        stroke="#0b3d24"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                    </svg>

                    support@mlainr.com

                  </span>

                </div>

              </div>

            </div>

          </div>

        </div>

        {/* ==========================================================
            FOOTER BAR
        ========================================================== */}

        <div className="log123-footer-bar">

          {/* Copyright */}

          <span className="log123-footer-copy">

            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                marginRight: 6,
                verticalAlign: "-2px",
              }}
            >
              <path
                d="M12 2 4 5v6c0 5.25 3.4 9.74 8 11 4.6-1.26 8-5.75 8-11V5l-8-3z"
                stroke="#FFD129"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>

            © 2025 MLA Inturi Nageswara Rao Official Portal.
            All Rights Reserved.

          </span>

          {/* ========================================================
              FOOTER LINKS

              Privacy + Terms remain normal links.
              Delete Account opens popup.
          ======================================================== */}

          <span className="log123-footer-links">

            {/* Privacy Policy - unchanged */}

            <a
              href={privacyPolicyPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="log123-footer-link"
            >
              Privacy Policy
            </a>

            <span className="log123-footer-link-divider">
              |
            </span>

            {/* Terms - unchanged */}

            <a
              href={termsConditionsPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="log123-footer-link"
            >
              Terms &amp; Conditions
            </a>

            <span className="log123-footer-link-divider">
              |
            </span>

            {/* DELETE ACCOUNT */}

            <button
              type="button"
              className="log123-footer-link log123-delete-account-link"
              onClick={openDeleteAccount}
            >
              Delete Your Account
            </button>

          </span>

          {/* Powered By */}

          <span className="log123-footer-powered">

            Powered by{" "}

            <strong className="log123-footer-brand">
              YSK Infotech Pvt. Ltd.
            </strong>

          </span>

        </div>

      </div>

      {/* ============================================================
          DELETE ACCOUNT MODAL
      ============================================================ */}

      {showDeleteModal && (
        <div
          className="log123-delete-overlay"
          onMouseDown={(e) => {
            if (
              e.target === e.currentTarget &&
              !deleteLoading
            ) {
              closeDeleteAccount();
            }
          }}
        >

          <div className="log123-delete-modal">

            {/* Close */}

            <button
              type="button"
              className="log123-delete-close"
              onClick={closeDeleteAccount}
              disabled={deleteLoading}
              aria-label="Close"
            >
              ×
            </button>

            {/* Title */}

            <h2>
              Delete Your Account
            </h2>

            {/* Description */}

            <p className="log123-delete-description">
              Enter the email address linked to your account.
              We'll process your deletion request from there.
            </p>

            {/* Form */}

            <form
              onSubmit={handleDeleteAccount}
              className="log123-delete-form"
              autoComplete="off"
            >

              <input
                type="email"
                value={deleteEmail}
                onChange={(e) => {
                  setDeleteEmail(e.target.value);

                  setDeleteResponseMessage("");
                  setDeleteMessageType("");
                }}
                placeholder="Enter your email"
                className="log123-delete-email"
                disabled={deleteLoading}
                autoFocus
              />

              {/* Response message */}

              {deleteResponseMessage && (
                <p
                  className={`log123-delete-message ${
                    deleteMessageType === "success"
                      ? "log123-delete-success"
                      : "log123-delete-error"
                  }`}
                >
                  {deleteResponseMessage}
                </p>
              )}

              {/* Buttons */}

              <div className="log123-delete-actions">

                <button
                  type="button"
                  className="log123-delete-cancel"
                  onClick={closeDeleteAccount}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="log123-delete-submit"
                  disabled={deleteLoading}
                >
                  {deleteLoading
                    ? "Submitting..."
                    : "Submit"}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </>
  );
}


// ================================================================
// BACK TO LOGIN
// ================================================================

const BackToLoginLink = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="log123-back-link"
  >
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M15 18l-6-6 6-6"
        stroke="#0b3d24"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>

    Back to Login
  </button>
);

export default Login;
